/* assets/section-menu-v2.js
   Unified navigation controller for sections/menu-v2.liquid.

   STANDALONE controller (decided in Task 6, confirmed live in Task 9/11):
   <mega-menu>'s own click handling (main.min.js) is hardcoded to
   .js-megamenu-btn / .js-megamenu-parent, neither of which this markup
   uses, and Modal/MegaMenu/the focus-trap class are not exposed on
   window.* -- there is nothing to extend or call into. This file owns
   #MenuV2 end to end, against the [data-*] contract from Task 1:

     nav.menu-v2#MenuV2 > [data-menu-root]
       [data-panel-trigger]  (aria-expanded + aria-controls)
       [data-panel]          (id matches its trigger's aria-controls)
       [data-panel-back]
       [data-menu-close]

   A5 correction: of the above, this file actually QUERIES
   [data-panel-trigger], [data-panel-back] and [data-menu-close] (see
   panelFor()/the click delegates below). [data-menu-root] and [data-panel]
   are markup documentation only -- nothing in this file selects on either
   attribute. Panels are resolved via panelFor(): document.getElementById(
   trigger.getAttribute('aria-controls')), matching each trigger to its
   panel by id, not by walking to a [data-menu-root] ancestor or querying
   [data-panel]. Left in the markup as useful hooks (structure/intent is
   still accurate documentation for a reader), but do not assume they are
   live selectors.

   Desktop (>=1024px): [data-panel-trigger] elements that are
   .menu-v2__bar-link--parent open a mega panel on hover-intent, click, or
   Enter/Space (native <button> semantics -- no extra keydown handler
   needed for that part). Only one panel is open at a time; only the
   trigger's own .menu-v2__panel is ever touched -- .menu-v2__col-drill
   triggers (which control nested .menu-v2__subpanel elements) are never
   queried by the desktop code path, so a desktop click/hover can never set
   `hidden` on a subpanel (the CSS makes `.menu-v2__subpanel[hidden]`
   display:block on desktop -- toggling it would be a silent no-op at best
   and is simply never attempted).

   Mobile (<1024px): the hamburger (id="menuV2Hamburger", see
   snippets/header-v2.liquid) opens/closes the drawer itself (is-active +
   is-visible on #MenuV2); any [data-panel-trigger] -- both
   .menu-v2__bar-link--parent AND .menu-v2__col-drill -- pushes its panel
   onto a stack; [data-panel-back] pops one level; [data-menu-close] and
   Escape close it. Same `hidden` attribute, same panelFor() lookup, same
   [data-panel-trigger] contract as desktop -- only the caller (mobile vs.
   desktop event listeners, each gated on isDesktop()) differs.

   Modal-conflict fix (Task 9/11, "Option B" from the task-11 brief):
   snippets/header-v2.liquid renders the hamburger as a plain <button>, NOT
   via the `modal-button` snippet -- it carries no data-behavior="modal",
   so main.min.js's global click listener never instantiates the theme's
   generic Modal class on #MenuV2. That Modal's default close-button
   selector ([data-behavior='close-modal']) matches nothing in this nav,
   so it could open the drawer but never reliably close it. This file is
   the only thing that ever touches #MenuV2's classes, so there is no
   double-toggle to guard against -- the hamburger is bound directly by id
   (menuV2Hamburger) rather than through a generic
   document.querySelector('[aria-controls="MenuV2"]') delegate, which
   sidesteps the ambiguity the brief flagged (the drawer's own
   [data-menu-close] button also carries aria-controls="MenuV2").

   Breakpoint reset: exactly one matchMedia('(min-width: 1024px)') change
   listener is registered per controller instance, near the end of init(),
   and it clears BOTH the desktop panel state and the mobile drawer/stack
   state. Every other listener in this file is guarded by
   isDesktop()/!isDesktop() so the two modes' handlers see each other's
   clicks/keys but always no-op on the wrong side of the breakpoint -- see
   the isDesktop() checks throughout.

   Section-reload lifecycle (Task 13, I5): Shopify's theme editor replaces
   a section's rendered markup on every settings change WITHOUT re-running
   <script defer> tags, so a plain top-level IIFE that captures #MenuV2
   once goes permanently dead (desktop AND mobile) after the first edit in
   the editor. Everything below is wrapped in init()/teardown() so the
   whole controller can be torn down and rebuilt against the fresh DOM on
   `shopify:section:load`, without ever double-binding a listener --
   init() tears down any previous live instance before building a new one,
   which also makes it safe to call more than once in a row. This mirrors
   the `Shopify.designMode && addEventListener('shopify:section:load', ...)`
   guard already used by assets/main.min.js, the assets/section-product*.
   min.js family and assets/addon-namedrop-modal.min.js -- real storefront
   visitors (designMode false/undefined) never register the extra
   listener. */

(function () {
  'use strict';

  var HOVER_DELAY = 120;                       // ms -- prevents strobing on diagonal pointer travel
  var CLOSE_DELAY = 200;                       // ms -- fix-wave 4: grace period on mouseleave so an
                                                // overshoot/corner-cut on the way into the panel (or a
                                                // stray sample landing in the geometric gap between the
                                                // tab bar and the panel -- see the bridge in
                                                // section-menu-v2.css) doesn't lose the menu outright.
                                                // Mirrors HOVER_DELAY's role/structure exactly, just for
                                                // the opposite edge (close-intent instead of open-intent).
  var DESKTOP_QUERY = '(min-width: 1024px)';

  // fix-wave 2 nit: window-scoped, not a local closure var. A local var only
  // guarantees idempotency across repeated calls to THIS execution of the
  // IIFE -- it relies on Shopify never re-running the <script> tag itself.
  // Storing the live teardown on `window` means even a genuine re-execution
  // of this file (a second, independent IIFE) still finds and tears down
  // whatever instance is already live before building its own, rather than
  // orphaning the first instance's document-level listeners.
  window.__menuV2Teardown = window.__menuV2Teardown || null;

  function init() {
    // Idempotent: if a previous instance is still wired up (e.g. two
    // shopify:section:load events landing back to back, or a stray direct
    // call), tear it down completely before building a new one so
    // listeners can never double-bind.
    if (window.__menuV2Teardown) {
      window.__menuV2Teardown();
      window.__menuV2Teardown = null;
    }

    var nav = document.getElementById('MenuV2');
    if (!nav) return;

    // The theme dims the page with a shared overlay element; header.liquid
    // and header-v2.liquid both declare data-overlay="underHeaderOverlay".
    // Task 10 flagged that the mobile drawer had no dimming of its own --
    // this file uses the same overlay/showOverlay() for both breakpoints.
    var overlay = document.getElementById('underHeaderOverlay');

    var hamburger = document.getElementById('menuV2Hamburger');

    // A4: assets/main.min.css only flips a transparent/inverted header
    // opaque (`.header--inverted.desktop-menu-is-open`) via a
    // MutationObserver watching `.header .js-megamenu-children` for an
    // `is-visible` class -- the OLD mega menu's contract. menu-v2 panels
    // are neither `.js-megamenu-children` nor class-toggled (they use the
    // `hidden` attribute via setPanelHidden()), so that observer never
    // fires for us. We drive the same class ourselves instead of trying
    // to satisfy the observer's markup contract.
    var header = document.getElementById('primaryHeader');

    var desktopMql = window.matchMedia(DESKTOP_QUERY);

    // Every listener this instance binds is recorded here so teardown()
    // can remove exactly what init() added -- nothing more, nothing less.
    var boundListeners = [];
    function addListener(target, type, fn) {
      target.addEventListener(type, fn);
      boundListeners.push([target, type, fn]);
    }

    function isDesktop() {
      return desktopMql.matches;
    }

    function panelFor(trigger) {
      return document.getElementById(trigger.getAttribute('aria-controls'));
    }

    function setPanelHidden(trigger, hidden) {
      var panel = panelFor(trigger);
      if (panel) panel.hidden = hidden;
      trigger.setAttribute('aria-expanded', hidden ? 'false' : 'true');
    }

    function showOverlay(show) {
      if (!overlay) return;
      overlay.classList.toggle('is-active', show);
      overlay.classList.toggle('is-visible', show);
    }

    function emit(name) {
      if (window.EventBus) window.EventBus.emit(name, { id: 'MenuV2' });
    }

    // ---- C1/C1b: body/html scroll lock ----
    // assets/main.min.css sets `html{overflow-y:scroll}` as a BASE rule
    // (no media query). Per the CSS Overflow spec, body's overflow only
    // propagates to the viewport when the ROOT element's own overflow
    // computes to `visible` -- it never does here, so the viewport takes
    // its overflow from <html>, and `body{overflow:hidden}` alone merely
    // clips a height:auto body box that has nothing to clip. Locking
    // documentElement too is what actually stops the viewport scrolling.
    //
    // scrollLocked tracks whether THIS controller is the one holding the
    // lock, so the release is conditional on that -- closeDrawer() (and
    // teardown()) must never blindly clear body/html overflow, because a
    // breakpoint-reset resetAll() runs closeDrawer() even when the drawer
    // was never open, and unconditionally clearing overflow there could
    // silently unlock a third-party (Klaviyo/PageFly) lock that happens to
    // also be sitting on body/html at that moment.
    var scrollLocked = false;

    function lockScroll() {
      if (scrollLocked) return;
      scrollLocked = true;
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    function unlockScroll() {
      if (!scrollLocked) return;
      scrollLocked = false;
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    // ---- fix-wave 2 nit: same asymmetry, applied to the shared overlay ----
    // #underHeaderOverlay is shared with the theme's OWN <mega-menu> search
    // dropdown (data-overlay="underHeaderOverlay" on .header__search-btn in
    // header-v2.liquid) -- an unconditional showOverlay(false) from
    // closeDrawer()/closeDesktopPanel() could hide it out from under that
    // unrelated feature if it happened to be the one showing it. Guarded the
    // same way scrollLocked guards unlockScroll(): only release the overlay
    // if THIS controller is the one that raised it.
    var overlayLocked = false;

    function lockOverlay() {
      overlayLocked = true;
      showOverlay(true);
    }

    function unlockOverlay() {
      if (!overlayLocked) return;
      overlayLocked = false;
      showOverlay(false);
    }

    // ================= desktop: hover-intent mega panels =================

    var hoverTimer = null;
    var closeTimer = null;   // fix-wave 4: pending mouseleave-close, or null
    var desktopOpenTrigger = null;   // currently open top-level trigger, or null

    function openDesktopPanel(trigger) {
      if (desktopOpenTrigger === trigger) return;
      var wasOpen = !!desktopOpenTrigger;

      // fix-wave 4: a panel legitimately opening -- by hover, click, or
      // keyboard activation (native <button> Enter/Space fires a `click`,
      // which reaches openDesktopPanel() through the same path) -- must
      // cancel any close still pending from a previous mouseleave. Cleared
      // here rather than only in the mouseenter listener below because a
      // trigger can be activated without the mouse ever re-entering nav
      // (e.g. Tab to it, then Enter) -- that path never fires mouseenter,
      // so a stale close timer from an earlier hover session could
      // otherwise fire ~200ms later and close the panel this call just
      // opened.
      clearTimeout(closeTimer);
      closeTimer = null;

      if (desktopOpenTrigger) setPanelHidden(desktopOpenTrigger, true);
      setPanelHidden(trigger, false);
      desktopOpenTrigger = trigger;

      // Only fire the overlay/EventBus edges when transitioning from fully
      // closed -> open. Switching between tabs while one is already open is
      // NOT a close+reopen from the outside world's perspective -- it would
      // flicker the overlay and spam modalOpened/modalClosed on every tab
      // during a slow sweep across the bar.
      if (!wasOpen) {
        lockOverlay();
        emit('modalOpened');
        // A4: opaque the header the same way the old mega menu did while a
        // panel is open, so a transparent/inverted header over a hero
        // doesn't leave a near-black panel hanging below white-on-white
        // text/logo/icons.
        if (header) header.classList.add('desktop-menu-is-open');
      }
    }

    function closeDesktopPanel() {
      // fix-wave 4: clear unconditionally, before the early-return guard --
      // this is the single close path Escape, outside click, and
      // resetAll()/teardown all already run through, so it's also the one
      // place that guarantees a pending mouseleave-close timer can never
      // survive an immediate close from any of those or fire later against
      // a stale/closed/torn-down instance.
      clearTimeout(closeTimer);
      closeTimer = null;

      if (!desktopOpenTrigger) return;
      setPanelHidden(desktopOpenTrigger, true);
      desktopOpenTrigger = null;
      unlockOverlay();
      emit('modalClosed');
      // A4: this is the single close path (Escape, outside click,
      // focusin-leaves-nav, and resetAll()/teardown all run through it --
      // see the fix-wave 4 note above), so clearing the class here also
      // covers resetAll()/teardown without needing any code there.
      if (header) header.classList.remove('desktop-menu-is-open');
    }

    addListener(nav, 'mouseover', function (e) {
      if (!isDesktop()) return;
      var trigger = e.target.closest('.menu-v2__bar-link--parent');
      if (!trigger || trigger === desktopOpenTrigger) return;

      clearTimeout(hoverTimer);
      hoverTimer = setTimeout(function () {
        hoverTimer = null;
        openDesktopPanel(trigger);
      }, HOVER_DELAY);
    });

    // fix-wave 4: cancels a pending mouseleave-close (below) the instant the
    // pointer is back inside nav's DOM subtree -- which includes the panel
    // itself (position:fixed, but still a true DOM descendant of nav; see
    // the file-level comment on hover-intent), so re-entering over the
    // panel counts the same as re-entering over the bar. mouseenter is the
    // natural complement to mouseleave here: both are non-bubbling and each
    // fires exactly once per genuine crossing of nav's boundary, in either
    // direction, regardless of which descendant is under the pointer.
    addListener(nav, 'mouseenter', function () {
      if (!isDesktop()) return;
      clearTimeout(closeTimer);
      closeTimer = null;
    });

    addListener(nav, 'mouseleave', function () {
      if (!isDesktop()) return;
      clearTimeout(hoverTimer);
      hoverTimer = null;

      // fix-wave 4: a grace period instead of closing immediately. Even
      // with the geometric gap bridged (assets/section-menu-v2.css), a
      // fast pointer can still overshoot the panel's edge or cut a corner
      // on the way in -- mousemove is sampled discretely, so a stray
      // sample can land outside nav's bounds for one tick before the next
      // sample lands back inside it. Structured exactly like the
      // HOVER_DELAY open-intent timer above: clear any existing timer,
      // start a fresh one. Cancelled by mouseenter (above) if the pointer
      // returns before it fires; cleared unconditionally at the top of
      // closeDesktopPanel() (so Escape/outside-click/resetAll still close
      // instantly and can't leave this dangling) and again for good
      // measure below in case closeDesktopPanel() itself never runs for
      // some reason.
      clearTimeout(closeTimer);
      closeTimer = setTimeout(function () {
        closeTimer = null;
        closeDesktopPanel();
      }, CLOSE_DELAY);
    });

    addListener(nav, 'click', function (e) {
      if (!isDesktop()) return;
      var trigger = e.target.closest('.menu-v2__bar-link--parent');
      if (!trigger) return;

      e.preventDefault();
      clearTimeout(hoverTimer);
      hoverTimer = null;

      if (desktopOpenTrigger === trigger) {
        closeDesktopPanel();
      } else {
        openDesktopPanel(trigger);
      }
    });

    addListener(document, 'keydown', function (e) {
      if (e.key !== 'Escape' || !isDesktop() || !desktopOpenTrigger) return;
      var trigger = desktopOpenTrigger;
      closeDesktopPanel();
      // fix-wave 3 audit: no preventScroll needed -- .menu-v2's
      // overflow:clip (assets/section-menu-v2.css) that this bug lives
      // inside of is declared only in @media (max-width: 1023px); at
      // desktop widths nav has no overflow property at all (default:
      // visible), so it was never a scroll container here to begin with,
      // gated or not. `trigger` is also always on-screen in the top bar
      // regardless.
      trigger.focus();
    });

    addListener(document, 'click', function (e) {
      if (!isDesktop() || !desktopOpenTrigger) return;
      if (!nav.contains(e.target)) closeDesktopPanel();
    });

    // Close when focus leaves the nav entirely (keyboard users tabbing past
    // the last link in an open panel out into the rest of the page).
    addListener(document, 'focusin', function (e) {
      if (!isDesktop() || !desktopOpenTrigger) return;
      if (!nav.contains(e.target)) closeDesktopPanel();
    });

    // Arrow keys move between links inside the currently open panel.
    addListener(nav, 'keydown', function (e) {
      if (!isDesktop() || !desktopOpenTrigger) return;
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;

      var panel = panelFor(desktopOpenTrigger);
      if (!panel || !panel.contains(document.activeElement)) return;

      var links = [].slice.call(panel.querySelectorAll('a[href]'));
      var i = links.indexOf(document.activeElement);
      if (i === -1) return;

      e.preventDefault();
      var next = e.key === 'ArrowDown' ? i + 1 : i - 1;
      if (next < 0) next = links.length - 1;
      if (next >= links.length) next = 0;
      // fix-wave 3 audit: deliberately NOT preventScroll. Same desktop
      // no-scroll-container reasoning as the Escape handler above, but
      // also: .menu-v2__panel is `overflow-y: auto` on desktop for long
      // columns, and arrow-key navigation SHOULD auto-scroll that panel to
      // reveal a newly-focused link that's currently scrolled out of view
      // -- preventScroll would suppress that wanted behavior along with
      // any unwanted one.
      links[next].focus();
    });

    // ===================== mobile: drill-down drawer ======================

    var mobileStack = [];   // open panel triggers, innermost last

    // I4: the stack is keyed by DOM depth, not just push/pop order.
    // .menu-v2__bar-link--parent triggers a top-level panel (depth 0);
    // .menu-v2__col-drill triggers a group's subpanel one level in (depth
    // 1). Everything else defensively falls back to "next slot" rather
    // than throwing.
    function depthOf(trigger) {
      if (trigger.classList.contains('menu-v2__bar-link--parent')) return 0;
      if (trigger.classList.contains('menu-v2__col-drill')) return 1;
      return mobileStack.length;
    }

    function pushPanel(trigger) {
      var panel = panelFor(trigger);
      if (!panel) return;

      // I4: a trigger already open somewhere in the stack (a double-tap on
      // the same row while its 400ms slide is still in flight) must be a
      // no-op -- pushing it again would leave two copies of the same
      // trigger on the stack, and a later Back would pop the phantom
      // duplicate instead of actually closing anything.
      if (mobileStack.indexOf(trigger) !== -1) return;

      // I4: two sibling triggers at the same depth (e.g. tapping "Men's"
      // then "Women's" mid-slide, both depth 0) must replace one another,
      // not both end up open. Pop back to this trigger's own depth first --
      // hiding without focusing, since pushPanel is about to move focus
      // into the panel it's opening anyway.
      var depth = depthOf(trigger);
      while (mobileStack.length > depth) {
        setPanelHidden(mobileStack.pop(), true);
      }

      setPanelHidden(trigger, false);
      mobileStack.push(trigger);

      // Land focus inside the panel that just slid in, on its own back row --
      // the natural first stop, and it keeps the focus trap coherent instead
      // of leaving focus on a trigger row now covered by the new panel.
      //
      // fix-wave 3: { preventScroll: true } is the PRIMARY fix for a real,
      // confirmed bug -- this call fires the instant setPanelHidden() above
      // clears the panel's `hidden` attribute, before its
      // translateX(100%)->0 slide-in has visually progressed. At that exact
      // instant the panel is still rendered off to the right, and .menu-v2
      // used to be `overflow: hidden` -- which still makes an element a
      // scroll container, and focusing a descendant that's currently
      // rendered outside a scroll container's own viewport makes the
      // browser auto-scroll that container to reveal it. Confirmed live at
      // 390x844: tapping any tab left the drawer permanently blank because
      // nav itself scrolled horizontally by one full drawer width
      // (nav.scrollLeft 390 of scrollWidth 780), dragging the root list and
      // every panel off-screen together. .menu-v2 is now `overflow: clip`
      // (assets/section-menu-v2.css), which removes nav's scroll-container
      // capability entirely and makes this class of bug structurally
      // impossible on its own -- preventScroll is kept here anyway as
      // defense in depth against any OTHER scroll container that might
      // exist in the ancestor chain (e.g. outside nav) now or in the
      // future.
      var back = panel.querySelector('[data-panel-back]');
      if (back) back.focus({ preventScroll: true });

      // Belt-and-braces: guarantee nav can never be left in a scrolled
      // state, even from some other code path focusing something
      // off-canvas. Redundant with overflow:clip (which pins these at 0
      // unconditionally) but free, and documents the invariant explicitly
      // in case that CSS property is ever reverted without this also being
      // reconsidered.
      nav.scrollLeft = 0;
      nav.scrollTop = 0;
    }

    function popPanel() {
      var trigger = mobileStack.pop();
      if (!trigger) return;
      setPanelHidden(trigger, true);
      // fix-wave 3: audited, not actively hazardous -- `trigger` is a
      // static, never-transformed button (only its PANEL slides; the
      // trigger row itself never moves), so it's always on-screen at focus
      // time regardless of pop depth. preventScroll added anyway as
      // free, zero-behavior-change insurance, matching back.focus() above.
      trigger.focus({ preventScroll: true });
      nav.scrollLeft = 0;
      nav.scrollTop = 0;
    }

    function resetPanels() {
      while (mobileStack.length) {
        setPanelHidden(mobileStack.pop(), true);
      }
    }

    function openDrawer() {
      nav.classList.add('is-active', 'is-visible');
      nav.setAttribute('role', 'dialog');
      nav.setAttribute('aria-modal', 'true');
      document.body.classList.add('mobile-menu-open');
      lockScroll();
      if (hamburger) hamburger.setAttribute('aria-expanded', 'true');

      // Dim the page behind the drawer too -- Task 10 flagged that without
      // this, tablet-width viewports (drawer is min(90vw, 25rem) wide) leave
      // exposed, tappable page content beside the open drawer.
      lockOverlay();

      // fix-wave 3: audited, not actively hazardous -- closeBtn sits in
      // .menu-v2__drawer-header, an in-flow child that is never itself
      // transformed (only the SLIDING PANELS are), and the page's own
      // scroll is already locked (lockScroll() above) before this call, so
      // there's nothing left that could try to auto-scroll to reveal it
      // either. preventScroll added anyway as free insurance, and to
      // guarantee a clean scrollLeft:0 start every time the drawer opens.
      var closeBtn = nav.querySelector('[data-menu-close]');
      if (closeBtn) closeBtn.focus({ preventScroll: true });
      nav.scrollLeft = 0;
      nav.scrollTop = 0;

      emit('modalOpened');
    }

    function closeDrawer() {
      var wasOpen = nav.classList.contains('is-visible');

      nav.classList.remove('is-active', 'is-visible');
      nav.removeAttribute('role');
      nav.removeAttribute('aria-modal');
      document.body.classList.remove('mobile-menu-open');
      unlockScroll();
      if (hamburger) hamburger.setAttribute('aria-expanded', 'false');
      unlockOverlay();

      // Closing always resets to the root panel, regardless of how deep the
      // stack was.
      resetPanels();
      // Belt-and-braces, same reasoning as pushPanel()/popPanel()/
      // openDrawer(): a closed drawer should never carry a stale scroll
      // offset into its next open.
      nav.scrollLeft = 0;
      nav.scrollTop = 0;

      if (wasOpen) {
        // fix-wave 3 audit: no preventScroll -- hamburger lives in the
        // site header (header-v2.liquid), OUTSIDE #MenuV2 entirely, so
        // it was never inside the drawer's own (now-removed) scroll
        // container to begin with, and the header is sticky so it's
        // always in view regardless of page scroll position.
        //
        // A5: closeDrawer() is also called from resetAll() on a
        // mobile->desktop breakpoint change, at which point
        // .header__mobile-menu-cta (the hamburger's wrapper) is already
        // display:none (see the @media (min-width: 1024px) rule in
        // assets/section-menu-v2.css). Focusing a display:none element is
        // a silent no-op that drops focus to <body> instead of moving it
        // anywhere useful. Guarded the same way the Tab-trap focusable
        // filter below already checks visibility, so the focus only
        // lands when the hamburger is actually there to receive it.
        if (hamburger && hamburger.offsetParent !== null) hamburger.focus();
        emit('modalClosed');
      }
    }

    if (hamburger) {
      addListener(hamburger, 'click', function (e) {
        if (isDesktop()) return;
        e.preventDefault();
        if (nav.classList.contains('is-visible')) {
          closeDrawer();
        } else {
          openDrawer();
        }
      });
    }

    // Tapping the shared overlay dismisses the drawer, matching the site's
    // existing convention (the theme's own Modal closes on overlay click;
    // .overlay is cursor:pointer sitewide for exactly this reason). No-ops
    // whenever the drawer isn't the thing currently showing the overlay.
    if (overlay) {
      addListener(overlay, 'click', function () {
        if (isDesktop() || !nav.classList.contains('is-visible')) return;
        closeDrawer();
      });
    }

    addListener(nav, 'click', function (e) {
      if (isDesktop()) return;

      if (e.target.closest('[data-panel-back]')) {
        e.preventDefault();
        popPanel();
        return;
      }

      if (e.target.closest('[data-menu-close]')) {
        e.preventDefault();
        closeDrawer();
        return;
      }

      var trigger = e.target.closest('[data-panel-trigger]');
      if (trigger) {
        e.preventDefault();
        pushPanel(trigger);
      }
    });

    addListener(document, 'keydown', function (e) {
      if (e.key !== 'Escape' || isDesktop()) return;
      if (mobileStack.length) {
        popPanel();
      } else if (nav.classList.contains('is-visible')) {
        closeDrawer();
      }
    });

    // Focus trap while the drawer is open.
    addListener(document, 'keydown', function (e) {
      if (e.key !== 'Tab' || isDesktop() || !nav.classList.contains('is-visible')) return;

      var focusable = nav.querySelectorAll(
        'a[href], button:not([disabled]), input, [tabindex]:not([tabindex="-1"])'
      );
      // C2: offsetParent !== null only nulls out for display:none,
      // position:fixed, or root/body -- it does NOT null for
      // visibility:hidden, which is how a closed mobile panel/subpanel is
      // hidden (display stays block so the slide-in transition has
      // something to animate; see section-menu-v2.css). Every trigger's
      // panel/subpanel carries the real `hidden` attribute while closed
      // (setPanelHidden() sets panel.hidden, not a CSS-only class), so
      // excluding anything inside a [hidden] ancestor is what actually
      // filters out the offscreen, visibility:hidden content -- without
      // it, `last` resolves to a permanently invisible element (forward
      // Tab escapes the drawer) and `first` can end up being a
      // visibility:hidden node (Shift+Tab's .focus() becomes a no-op and
      // focus drops to <body>). checkVisibility() is deliberately not used
      // here -- not broadly supported enough for this theme's audience.
      var visible = [].filter.call(focusable, function (el) {
        return el.offsetParent !== null && !el.closest('[hidden]');
      });
      if (!visible.length) return;

      var first = visible[0];
      var last = visible[visible.length - 1];

      // fix-wave 3 audit: deliberately NOT preventScroll on either wrap
      // call. These fire on a Tab press while the drawer is already fully
      // open and stable -- unlike pushPanel()'s back.focus(), `first`/
      // `last` are never mid-transition/off-canvas at this point (the
      // panel that contains them already finished sliding in before a user
      // could tab to its edges), so the horizontal hazard this fix wave is
      // about doesn't apply here regardless, and .menu-v2's overflow:clip
      // (assets/section-menu-v2.css) now makes it structurally impossible
      // either way. `last`, though, can legitimately be scrolled out of
      // view within a long open panel's OWN overflow-y:auto -- Tab-ing to
      // it SHOULD auto-scroll that panel to reveal it (the same reason the
      // desktop arrow-key handler above also skips preventScroll);
      // preventScroll would suppress that wanted behavior too, since it
      // blocks auto-scroll on every scrollable ancestor, not just nav.
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    });

    // ============ single breakpoint-reset handler (registered once per instance) ============
    // Crossing 1024px in either direction must not leave stale state behind:
    // an open desktop panel surviving a narrowing resize would sit there
    // with the overlay up and no way to reach it; an open mobile drawer
    // surviving a widening resize would leave is-active/is-visible on #MenuV2
    // and the body scroll-locked with no drawer visibly open to close it.

    function resetAll() {
      clearTimeout(hoverTimer);
      hoverTimer = null;
      // fix-wave 4: closeDesktopPanel() below already clears closeTimer
      // unconditionally (see its own top), so this is belt-and-braces --
      // explicit here too, mirroring the hoverTimer line right above it,
      // so a reader doesn't have to chase into closeDesktopPanel() to
      // confirm the delay timer can't survive a breakpoint reset or
      // teardown.
      clearTimeout(closeTimer);
      closeTimer = null;
      closeDesktopPanel();
      closeDrawer();
    }

    addListener(desktopMql, 'change', resetAll);

    window.__menuV2Teardown = function () {
      // Release any lock/overlay/open state before ripping listeners out
      // from under a live drawer -- the theme editor can swap this
      // section's markup mid-interaction (drawer open, panel pushed).
      resetAll();
      for (var i = 0; i < boundListeners.length; i++) {
        boundListeners[i][0].removeEventListener(boundListeners[i][1], boundListeners[i][2]);
      }
      boundListeners.length = 0;
    };
  }

  init();

  // I5: theme-editor only. A plain <script defer> never re-runs when the
  // editor replaces this section's markup, so without this the nav goes
  // completely dead (desktop AND mobile) after any settings change until a
  // full page reload -- that is how a merchant experiences every edit to
  // this section. init() itself tears down the previous instance first
  // (see the comment at its top), so re-running it here on every
  // shopify:section:load is safe and cannot double-bind listeners.
  //
  // fix-wave 2, Item 1: shopify:section:unload fires for ANY section on the
  // page unloading, not just this one -- there is no section-id/containment
  // check available to narrow it (the event carries no reference to
  // #MenuV2 itself). Re-rendering some OTHER section is harmless either
  // way (its unload tears us down, its own load fires separately and never
  // touches us), but DELETING some other section fires unload with no
  // matching load ever following -- a bare teardown() here would leave the
  // nav permanently dead until the merchant happened to edit something
  // else. Calling init() instead of a bare teardown handles both cases with
  // one code path: init() already tears down any live instance up front
  // (see its own top), then re-queries #MenuV2 -- if THIS section is what
  // got deleted, the element is gone and init() no-ops (confirmed: it
  // returns right after `if (!nav) return;`, leaving
  // window.__menuV2Teardown null, nothing left dangling); if it was some
  // unrelated section, #MenuV2 is untouched and init() simply rebuilds a
  // fresh, correctly-wired instance against it.
  // Real storefront visitors (designMode false/undefined) never register
  // either listener -- same guard assets/main.min.js, the
  // assets/section-product*.min.js family and
  // assets/addon-namedrop-modal.min.js already use for this exact problem.
  if (window.Shopify && window.Shopify.designMode) {
    window.addEventListener('shopify:section:load', init);
    window.addEventListener('shopify:section:unload', init);
  }
})();
